import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';

import Memory from 'components/tribut/MemorySection/Memory/Memory';
import Loading from 'components/UI/Loading/Loading';

import classes from './MemoriesCarousel.module.css';

const MemoriesCarousel = props => {
  const navigate = useNavigate();
  const [localMemories, setLocalMemories] = useState(
    props.memories || []
  );

  useEffect(() => {
    if (props.memories) {
      setLocalMemories(props.memories);
    }
  }, [props.memories]);

  const total = localMemories.length;

  const getInitialIndex = () => {
    if (!props.memories || props.memories.length === 0) return 0;
    const found = props.memories.findIndex(
      m => Number(m.memoryId) === Number(props.memoryView)
    );
    return found >= 0 ? found : 0;
  };

  const [currentIndex, setCurrentIndex] = useState(getInitialIndex);
  const [isFullScreen, setIsFullScreen] = useState(false);

  useEffect(() => {
    if (localMemories && localMemories.length > 0) {
      const idx = localMemories.findIndex(
        m => Number(m.memoryId) === Number(props.memoryView)
      );
      if (idx >= 0 && idx !== currentIndex) {
        setCurrentIndex(idx);
      }
    }
  }, [props.memoryView, localMemories]);

  const updateUrl = memory => {
    if (memory && memory.memoryId && props.tributId) {
      window.history.replaceState(
        null,
        '',
        `/tribut/${props.tributId}/memories/${memory.memoryId}`
      );
    }
  };

  const prevMemoryHandler = () => {
    if (total <= 1) return;
    const prevIdx = (currentIndex - 1 + total) % total;
    setCurrentIndex(prevIdx);
    updateUrl(localMemories[prevIdx]);
  };

  const nextMemoryHandler = () => {
    if (total <= 1) return;
    const nextIdx = (currentIndex + 1) % total;
    setCurrentIndex(nextIdx);
    updateUrl(localMemories[nextIdx]);
  };

  const selectMemoryHandler = targetIndex => () => {
    if (targetIndex >= 0 && targetIndex < total) {
      setCurrentIndex(targetIndex);
      updateUrl(localMemories[targetIndex]);
    }
  };

  const handleDeleteMemory = deletedId => {
    const remaining = localMemories.filter(
      m => Number(m.memoryId) !== Number(deletedId)
    );
    setLocalMemories(remaining);

    if (typeof props.onDeleteMemory === 'function') {
      props.onDeleteMemory(deletedId);
    }

    if (remaining.length === 0) {
      if (typeof props.changeView === 'function') {
        props.changeView('all');
      } else if (props.tributId) {
        navigate(
          props.tributId !== 73
            ? `/tribut/${props.tributId}/memorias`
            : '/alvaro'
        );
      }
      return;
    }

    let nextIndex = currentIndex;
    if (nextIndex >= remaining.length) {
      nextIndex = Math.max(0, remaining.length - 1);
    }
    setCurrentIndex(nextIndex);
    updateUrl(remaining[nextIndex]);
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => nextMemoryHandler(),
    onSwipedRight: () => prevMemoryHandler()
  });

  if (total === 0) {
    return <Loading />;
  }

  const currentMemory =
    localMemories[currentIndex] || localMemories[0];

  const visibleThumbnails = [];
  if (total <= 5) {
    for (let i = 0; i < total; i++) {
      visibleThumbnails.push({
        memory: localMemories[i],
        index: i,
        isActive: i === currentIndex
      });
    }
  } else {
    // Exactly 5 thumbnails, with active item always centered at offset 0 (index 2)
    const offsets = [-2, -1, 0, 1, 2];
    offsets.forEach(offset => {
      const itemIdx = (currentIndex + offset + total) % total;
      visibleThumbnails.push({
        memory: localMemories[itemIdx],
        index: itemIdx,
        isActive: offset === 0
      });
    });
  }

  const sliderComponent = (
    <div className={classes.carouselSliderWrapper}>
      {visibleThumbnails.map((thumb, pos) => (
        <figure
          key={`thumb-${thumb.memory.memoryId}-${pos}`}
          onClick={selectMemoryHandler(thumb.index)}
          className={
            thumb.isActive
              ? classes.memorySelectActive
              : classes.memorySelect
          }
          aria-label={`Ver memoria ${thumb.memory.memoryId}`}
        >
          <img
            src={thumb.memory.thumbnail}
            alt={thumb.memory.mediaType || 'thumbnail'}
            className={classes.img}
            loading="lazy"
          />
        </figure>
      ))}
    </div>
  );

  return (
    <div className={classes.memoriesContainer}>
      <div {...handlers}>
        {currentMemory && (
          <div
            className={classes.memoryContainer}
            id={`memory-${currentMemory.memoryId}`}
            key={`memory-active-${currentMemory.memoryId}`}
          >
            <Memory
              dataMemory={currentMemory}
              tributId={Number(props.tributId)}
              memoryView={currentMemory.memoryId}
              prevMemoryHandler={prevMemoryHandler}
              nextMemoryHandler={nextMemoryHandler}
              carousel={sliderComponent}
              isFullScreen={isFullScreen}
              onToggleFullScreen={setIsFullScreen}
              onDeleteMemory={handleDeleteMemory}
              loadData={props.loadData}
            />
          </div>
        )}
      </div>
    </div>
  );
};

MemoriesCarousel.propTypes = {
  // eslint-disable-next-line react/require-default-props
  initialMemory: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  newMemories: PropTypes.array,
  // eslint-disable-next-line react/require-default-props
  tributId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  // eslint-disable-next-line react/require-default-props
  memoryView: PropTypes.number,
  // eslint-disable-next-line react/require-default-props
  loadMoreMemoriesHandler: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  memories: PropTypes.array,
  // eslint-disable-next-line react/require-default-props
  className: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  onClick: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  onDeleteMemory: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  changeView: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  loadData: PropTypes.func
};

export default MemoriesCarousel;
